import { Suspense, useMemo, useRef, useEffect, useState, ComponentType } from 'react';
import { useLocation, Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { CurrentMenuProvider } from '@/contexts/CurrentMenuContext';
import { componentMap, getComponentByKey } from '@/config/routeComponents';
import { recordMenuAccess } from '@/lib/auditBeacon';
import { startInteractionAudit } from '@/lib/auditInteractions';
import AppLayout from '@/components/AppLayout';
import NotFound from '@/pages/NotFound';

// Loading component for content area only - not full page
const ContentLoader = () => (
  <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
  </div>
);

// Full page loader for initial auth check
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
  </div>
);

// Find a menu anywhere in the tree by id (used to resolve a sub-menu's parent)
const findMenuById = (menus: any[], id: string): any | null => {
  for (const menu of menus) {
    if (menu.id === id) return menu;
    if (menu.children?.length > 0) {
      const found = findMenuById(menu.children, id);
      if (found) return found;
    }
  }
  return null;
};

/**
 * DynamicRoute component
 *
 * This component handles dynamic routing based on menu configuration from the database.
 * It finds the appropriate component to render based on:
 * 1. The menu's componentKey (if specified in database)
 * 2. The last segment of the URL path
 * 3. Fallback strategies for nested paths
 */
export const DynamicRoute = () => {
  const location = useLocation();
  const { isAuthenticated, isLoading, getAllowedMenus } = useAuth();

  // Track if this is the first render (initial load)
  const isFirstRender = useRef(true);
  const [hasRenderedOnce, setHasRenderedOnce] = useState(false);

  // Store the last successfully rendered component to prevent blink
  const lastComponentRef = useRef<ComponentType | null>(null);

  // Find the matching menu item from user's allowed menus
  const { Component, menuItem, routeKey } = useMemo(() => {
    const currentPath = location.pathname.toLowerCase();
    const allowedMenus = getAllowedMenus();

    // Recursive function to find menu by path
    const findMenuByPath = (menus: any[], targetPath: string): any | null => {
      for (const menu of menus) {
        const menuPath = menu.path?.toLowerCase().replace(/\/$/, '');
        if (menuPath === targetPath) {
          return menu;
        }
        if (menu.children?.length > 0) {
          const found = findMenuByPath(menu.children, targetPath);
          if (found) return found;
        }
      }
      return null;
    };

    // Try to find exact match
    let foundMenu = findMenuByPath(allowedMenus, currentPath);

    // If not found, try without trailing slash variations
    if (!foundMenu) {
      const pathWithoutTrailing = currentPath.replace(/\/$/, '');
      foundMenu = findMenuByPath(allowedMenus, pathWithoutTrailing);
    }

    // If the URL uses the canonical hyphenated form but the DB menu was
    // stored without the hyphen (or vice versa), try the de-hyphenated path.
    if (!foundMenu) {
      const deHyphenated = currentPath.replace(/-/g, '');
      if (deHyphenated !== currentPath) {
        foundMenu = findMenuByPath(allowedMenus, deHyphenated);
      }
    }

    // Get component using componentKey from database or path-based resolution
    const componentKey = foundMenu?.componentKey || null;
    const path = location.pathname.toLowerCase();

    const forcePathMatch = () => {
      if (
        path.includes('/target_data') ||
        path.includes('/target-data') ||
        path.includes('/target_and_action') ||
        path.includes('/target-and-action') ||
        path.includes('/targetdata') ||
        path.includes('/targetandaction')
      ) {
        return componentMap['target_and_action'] || componentMap['target-data'] || componentMap['targetdata'] || componentMap['target_data'];
      }
      return undefined;
    };

    const forcedComponent = forcePathMatch();
    if (forcedComponent) {
      return {
        menuItem: foundMenu,
        Component: forcedComponent,
        routeKey: undefined as string | undefined,
      };
    }

    const resolvedComponent = getComponentByKey(componentKey, location.pathname);

    // A module whose path was edited in super-admin (e.g. /compliance-studio →
    // /custom-compliance) no longer matches by path. Only then fall back to the
    // menu's super-admin component key; the page receives it as `routeKey` so a
    // multi-page MFE can still pick the right screen. Paths that resolve today
    // never reach this branch.
    if (!resolvedComponent && foundMenu?.routeKey) {
      const byRouteKey = getComponentByKey(foundMenu.routeKey);
      if (byRouteKey) {
        return { menuItem: foundMenu, Component: byRouteKey, routeKey: foundMenu.routeKey as string };
      }
    }

    return {
      menuItem: foundMenu,
      Component: resolvedComponent,
      routeKey: undefined as string | undefined,
    };
  }, [location.pathname, getAllowedMenus]);

  // Update the last component ref when we have a valid component
  useEffect(() => {
    if (Component) {
      lastComponentRef.current = Component;
    } else {
      lastComponentRef.current = null;
    }
  }, [Component]);

  // Audit: log each menu the user opens, once per menu change (not per re-render or in-page tab)
  const lastLoggedMenuId = useRef<string | null>(null);
  useEffect(() => {
    if (!isAuthenticated || !menuItem?.id || lastLoggedMenuId.current === menuItem.id) return;
    lastLoggedMenuId.current = menuItem.id;
    const parent = menuItem.parentId ? findMenuById(getAllowedMenus(), menuItem.parentId) : null;
    recordMenuAccess(menuItem, parent?.label ?? null);
  }, [isAuthenticated, menuItem, getAllowedMenus]);

  // Audit: clicks, tab switches and dialogs inside the page, while signed in
  useEffect(() => (isAuthenticated ? startInteractionAudit() : undefined), [isAuthenticated]);

  // Mark first render as complete after mount
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      // Small delay to ensure component is fully mounted
      const timer = setTimeout(() => setHasRenderedOnce(true), 50);
      return () => clearTimeout(timer);
    }
  }, []);

  // Show loading state only during initial auth check
  if (isLoading) {
    return <PageLoader />;
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Determine which component to render
  const ComponentToRender = Component || lastComponentRef.current;

  // dMRV switches its own pages/tabs from the URL, so keep it mounted across
  // /dmrv/* navigation (no full remount = no blink). Other modules keep the
  // per-path key.
  const lowerPath = location.pathname.toLowerCase();
  const remountKey = lowerPath === '/dmrv' || lowerPath.startsWith('/dmrv/') ? '/dmrv' : location.pathname;

  // Always render AppLayout to keep sidebar/header mounted
  // Only the content inside changes on navigation - no blink
  return (
    <AppLayout>
      <CurrentMenuProvider menu={menuItem}>
        {ComponentToRender ? (
          <Suspense fallback={hasRenderedOnce ? null : <ContentLoader />}>
            {routeKey ? <ComponentToRender key={remountKey} routeKey={routeKey} /> : <ComponentToRender key={remountKey} />}
          </Suspense>
        ) : (
          <NotFound />
        )}
      </CurrentMenuProvider>
    </AppLayout>
  );
};

export default DynamicRoute;
